"use client";

import React, { useState, useEffect, Suspense } from "react";
import Image from "next/image";
import { login } from "@/services/authService";
import { isAuthenticated } from "@/utils/auth";

const REMEMBER_KEY = "rememberedEmail";

function getSafeRedirect(param: string | null): string {
  if (!param) return "/home";
  if (param.startsWith("/") && !param.startsWith("//")) return param;
  return "/home";
}

function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  useEffect(() => {
    if (hasCheckedAuth) return;
    const sp = new URLSearchParams(window.location.search);
    const hasRedirect = sp.has("redirect");
    const isExpired = sp.get("expired") === "true";

    if (hasRedirect || isExpired) {
      localStorage.removeItem("currentUser");
      setHasCheckedAuth(true);
      if (isExpired) setError("Sesión expirada. Ingresa de nuevo.");
    } else if (isAuthenticated()) {
      window.location.href = "/home";
      return;
    } else {
      setHasCheckedAuth(true);
    }

    const saved = localStorage.getItem(REMEMBER_KEY);
    if (saved) { setEmail(saved); setRememberMe(true); }
  }, [hasCheckedAuth]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login({ correo: email, password });
      if (result.success && result.token) {
        rememberMe
          ? localStorage.setItem(REMEMBER_KEY, email)
          : localStorage.removeItem(REMEMBER_KEY);
        const sp = new URLSearchParams(window.location.search);
        window.location.href = getSafeRedirect(sp.get("redirect"));
        return;
      }
      setError(result.error || "Credenciales incorrectas.");
    } catch {
      setError("Error de conexión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg, #f0fafa 0%, #e6f7f8 50%, #f8fffe 100%)" }}
    >
      {/* Decoración de fondo sutil */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, #0196A1, transparent)" }}
        />
        <div
          className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #0196A1, transparent)" }}
        />
      </div>

      {/* Tarjeta principal */}
      <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-xl shadow-[#0196A1]/8 border border-gray-100/80 p-8">

        {/* Logo */}
        <div className="flex justify-center mb-5">
          <Image
            src="/Imagenes/MiPymex_Logotipo.png"
            alt="Mi Pymex"
            width={160}
            height={72}
            priority
            style={{ objectFit: "contain" }}
          />
        </div>

        {/* PipeEnLinea badge */}
        <div className="flex justify-center mb-6">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#0196A1] bg-[#0196A1]/8 px-3 py-1 rounded-full tracking-wider uppercase">
            <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor">
              <rect x="2" y="14" width="5" height="7" rx="1" />
              <rect x="9.5" y="9" width="5" height="12" rx="1" />
              <rect x="17" y="3" width="5" height="18" rx="1" />
            </svg>
            PipeEnLinea
          </span>
        </div>

        {/* Divisor */}
        <div className="h-px bg-gray-100 mb-5" />

        {/* Título */}
        <div className="mb-5">
          <h1 className="text-lg font-semibold text-gray-800 tracking-tight">Iniciar sesión</h1>
          <p className="text-xs text-gray-400 mt-0.5">Ingresa tus credenciales para continuar</p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-5 flex items-start gap-2.5 p-3.5 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600">
            <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Correo */}
          <div>
            <label htmlFor="email" className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">
              Correo
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="usuario@correo.com"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder-gray-300
                focus:outline-none focus:bg-white focus:border-[#0196A1] focus:ring-4 focus:ring-[#0196A1]/10
                transition-all duration-200"
            />
          </div>

          {/* Contraseña */}
          <div>
            <label htmlFor="password" className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">
              Contraseña
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="••••••••"
                className="w-full px-4 py-3 pr-11 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder-gray-300
                  focus:outline-none focus:bg-white focus:border-[#0196A1] focus:ring-4 focus:ring-[#0196A1]/10
                  transition-all duration-200"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-300 hover:text-gray-500 transition-colors"
                aria-label={showPassword ? "Ocultar" : "Mostrar"}
              >
                {showPassword ? (
                  <svg className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                ) : (
                  <svg className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.477 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Recordar cuenta */}
          <label className="flex items-center gap-2.5 cursor-pointer group">
            <div
              onClick={() => setRememberMe(!rememberMe)}
              className={`w-4.5 h-4.5 w-[18px] h-[18px] rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all duration-150 ${
                rememberMe ? "bg-[#0196A1] border-[#0196A1]" : "border-gray-300 group-hover:border-[#0196A1]/60"
              }`}
            >
              {rememberMe && (
                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <span className="text-xs text-gray-400 select-none group-hover:text-gray-500 transition-colors">
              Recordar cuenta
            </span>
          </label>

          {/* Botón */}
          <button
            type="submit"
            disabled={loading}
            className="w-full mt-1 py-3.5 rounded-xl text-sm font-semibold text-white
              bg-[#0196A1] hover:bg-[#017a85] active:bg-[#015f69]
              disabled:opacity-50 disabled:cursor-not-allowed
              shadow-lg shadow-[#0196A1]/20 hover:shadow-[#0196A1]/30
              transition-all duration-200 flex items-center justify-center gap-2"
          >
            {loading ? (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : "Iniciar sesión"}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center text-[11px] text-gray-300 mt-6 leading-relaxed">
          © 2025 CAABSA · S.A. de C.V., SOFOM, E.N.R.<br />
          Todos los derechos reservados
        </p>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <svg className="animate-spin h-8 w-8 text-[#0196A1]" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    }>
      <LoginForm />
    </Suspense>
  );
}
